import { Badge } from "../ui/badge";
import { Card, CardTitle } from "../ui/card";
import { cn } from "../ui/cn";
import { TrendChart } from "./TrendChart";
import type { TileModel, TileProgress } from "./trends";

// The stat tiles of Home (CHT-2025), beside the dark card: one card per number the role may see, with the change against last week, a small
// chart and, for privacy, how far the oldest request is on the way to its target. It draws a `TileModel` (`trends.ts`) and decides nothing.
// The chart is decoration; the numbers are written in text for a screen reader.

function Progress({ progress }: Readonly<{ progress: TileProgress }>) {
  return (
    <div className="grid gap-1.5">
      <div aria-hidden="true" className="h-2 rounded-full bg-border">
        <div className="h-2 rounded-full bg-primary" style={{ width: `${progress.percent}%` }} />
      </div>
      <p className="m-0 text-xs leading-4 font-medium text-muted-foreground">{progress.text}</p>
    </div>
  );
}

type Chip = Readonly<{
  text: string;
  /** The change in words, for a screen reader. */
  words: string | null;
  tone: "success" | "neutral" | "outline" | "danger";
}>;

/** The chip of the header: the change against last week, or for privacy the days left. Nothing when the tile has neither. */
function chipOf(model: Extract<TileModel, { state: "ok" }>): Chip | null {
  if (model.change !== null) return model.change;
  if (model.progress !== null) return { ...model.progress.chip, words: null };
  return null;
}

function TileBody({ model }: Readonly<{ model: Extract<TileModel, { state: "ok" }> }>) {
  return (
    <>
      <div className="grid gap-0.5">
        <p className="m-0 font-serif text-[44px] leading-[46px] font-medium">{model.value}</p>
        <p className="m-0 text-sm leading-5 text-muted-foreground">{model.caption}</p>
      </div>
      {model.chart === null ? null : <TrendChart chart={model.chart} />}
      {model.progress === null ? null : <Progress progress={model.progress} />}
      <p className="sr-only">{model.spoken}</p>
    </>
  );
}

function Tile({ model }: Readonly<{ model: TileModel }>) {
  const headingId = `home-tile-${model.key}`;
  const chip = model.state === "ok" ? chipOf(model) : null;
  // The number sits between the header and the chart; a tile with no chart keeps it under the header.
  const drawn = model.state === "ok" && (model.chart !== null || model.progress !== null);
  return (
    <Card
      // A group with a name: a screen reader announces "Open requests, group" and then the numbers.
      role="group"
      aria-labelledby={headingId}
      className={cn(
        "flex min-h-[200px] flex-col gap-4 p-5 compact:gap-3 compact:p-4",
        drawn && "justify-between",
      )}
    >
      <div className="flex min-h-[26px] items-center justify-between gap-2">
        <CardTitle
          as="h2"
          id={headingId}
          className="font-sans text-sm leading-5 font-medium text-muted-foreground"
        >
          {model.title}
        </CardTitle>
        {chip === null ? null : (
          <Badge tone={chip.tone} className="font-semibold">
            {chip.text}
            {chip.words === null ? null : <span className="sr-only"> ({chip.words})</span>}
          </Badge>
        )}
      </div>
      {model.state === "unavailable" ? (
        <p className="m-0 text-sm leading-5 text-muted-foreground">
          This number could not be read. Try again later.
        </p>
      ) : (
        <TileBody model={model} />
      )}
    </Card>
  );
}

export function StatTiles({ models }: Readonly<{ models: readonly TileModel[] }>) {
  return (
    <div className="grid gap-3 sm:grid-cols-[repeat(auto-fill,minmax(168px,1fr))]">
      {models.map((model) => (
        <Tile key={model.key} model={model} />
      ))}
    </div>
  );
}
